import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { FiPaperclip, FiUploadCloud, FiLoader, FiTrash2, FiDownload } from 'react-icons/fi'

// Anexos de uma entidade (boleto/nota/comprovante). Lista + upload + download + excluir.
// Download vai por fetch autenticado (o endpoint exige Bearer) → blob → nova aba.
type EntityType = 'transaction' | 'sale' | 'transfer'
interface Att { id: number; filename: string; mime: string | null; size_bytes: number | null; created_at: string }

const fmtSize = (b: number | null) => (b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`)

export function AttachmentsSection({ entityType, entityId }: { entityType: EntityType; entityId: number }) {
  const { authenticatedFetch } = useAuth()
  const [rows, setRows] = useState<Att[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      const d = await authenticatedFetch(`/api/attachments?entity_type=${entityType}&entity_id=${entityId}`).then(r => r.json())
      if (d.success) setRows(d.attachments)
    } catch { /* silencioso */ }
  }, [authenticatedFetch, entityType, entityId])
  useEffect(() => { load() }, [load])

  const upload = async (file: File) => {
    setBusy(true); setError(null)
    try {
      const fd = new FormData()
      fd.append('file', file); fd.append('entity_type', entityType); fd.append('entity_id', String(entityId))
      const d = await authenticatedFetch('/api/attachments', { method: 'POST', body: fd }).then(r => r.json())
      if (!d.success) { setError(d.error || 'Falha ao anexar'); return }
      await load()
    } catch { setError('Erro ao enviar o anexo') }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = '' }
  }

  const download = async (a: Att) => {
    try {
      const r = await authenticatedFetch(`/api/attachments/${a.id}/download`)
      if (!r.ok) { setError('Falha ao baixar'); return }
      const url = URL.createObjectURL(await r.blob())
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { setError('Erro ao baixar o anexo') }
  }

  const del = async (a: Att) => {
    if (!confirm(`Excluir o anexo "${a.filename}"?`)) return
    const d = await authenticatedFetch(`/api/attachments/${a.id}`, { method: 'DELETE' }).then(r => r.json())
    if (d.success) load(); else setError(d.error || 'Falha ao excluir')
  }

  return (
    <div className="neu-convex rounded-lg border border-border p-3">
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center gap-1.5 text-sm font-medium text-foreground"><FiPaperclip className="w-4 h-4" /> Anexos {rows.length > 0 && <span className="text-xs text-muted-foreground">({rows.length})</span>}</label>
        <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.xml,.xls,.xlsx,.csv" onChange={e => { const f = e.target.files?.[0]; if (f) upload(f) }} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          className="neu-convex inline-flex items-center gap-1.5 rounded-lg border border-border text-xs font-medium px-2.5 py-1.5 text-foreground hover:bg-secondary disabled:opacity-60">
          {busy ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiUploadCloud className="w-3.5 h-3.5" />} Anexar
        </button>
      </div>
      {error && <div className="text-xs text-red-500 mb-2">{error}</div>}
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum anexo. Boleto, nota ou comprovante (PDF/imagem/planilha, até 20 MB).</p>
      ) : (
        <ul className="space-y-1">
          {rows.map(a => (
            <li key={a.id} className="flex items-center gap-2 text-sm">
              <button type="button" onClick={() => download(a)} className="flex items-center gap-1.5 min-w-0 flex-1 text-left text-primary hover:underline">
                <FiDownload className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{a.filename}</span>
              </button>
              {a.size_bytes != null && <span className="text-[11px] text-muted-foreground shrink-0">{fmtSize(a.size_bytes)}</span>}
              <button type="button" onClick={() => del(a)} title="Excluir" className="w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 shrink-0"><FiTrash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
